import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import Cookies from 'js-cookie'
import { isEmpty, isEqual, isNil } from 'lodash'
import PropTypes from 'prop-types'
import { batch, connect } from 'react-redux'

import * as Model from 'api/model'
import { setGroupCollapsed, setGroupExpanded } from 'drawer/tabs/layers/actions'
import Group from 'drawer/tabs/layers/groups/Group'

const styles = theme => ({
  root: {
    width: '100%',
    position: 'relative',
    top: 0,
    bottom: 0,
    overflowX: 'hidden',
    overflowY: 'hidden'
  }
})

class Groups extends React.Component {
  componentDidMount () {
    this.setGroupsExpandedState()
  }

  // Store cookie with expanded group ids
  componentDidUpdate = (prevProps) => {
    const { groups: nextGroups = {} } = this.props
    const { groups: prevGroups = {} } = prevProps

    if (!isEqual(nextGroups, prevGroups)) {
      // console.debug('componentDidUpdate - groups differ', isEmpty(prevGroups), isEmpty(nextGroups), prevGroups, nextGroups)

      // Call setGroupsExpandedState if the previous groups were empty and the next groups are not.
      // This can happen when the component had no groups when it was first rendered.
      if (isEmpty(prevGroups) && !isEmpty(nextGroups)) {
        this.setGroupsExpandedState()
        return
      }

      // Create an array of expanded group names from previous and next group
      const nextExpandedGroups = Object.keys(nextGroups).filter(groupid => nextGroups[groupid].expanded)
      const prevExpandedGroups = Object.keys(prevGroups).filter(groupid => prevGroups[groupid].expanded)

      // If the new/updated expanded groups differ from the stored groups, set an updated cookie. Do
      // nothing if there was no change.
      if (isEqual(nextExpandedGroups, prevExpandedGroups)) {
        return
      }

      const cookieOptions = {
        expires: 31 // in days
      }

      // Create string representation of array and store in cookie
      const nextExpandedGroupsJson = JSON.stringify(nextExpandedGroups)
      Cookies.set('pozi-state-groups-expanded', nextExpandedGroupsJson, cookieOptions)

      // console.debug('componentDidUpdate - stored new cookie', nextExpandedGroupsJson, cookieOptions)
    }
  }

  /**
   * Sets the expanded state of all groups based on the following rules:
   * 1. If the total number of layers is less than
   *    `config.layout.drawer.tabs.layers.numLayersAllGroupsCollapse` (default 40),
   *    then set all groups to be expanded by default.
   * 2. Get the list of expanded groups from the cookie
   *    `pozi-state-groups-expanded` and set the corresponding groups to be expanded.
   *
   */
  setGroupsExpandedState () {
    const { dispatchSetGroupCollapsed, dispatchSetGroupExpanded, groups } = this.props

    // console.debug('Groups - setGroupsExpandedState', groups)

    if (isNil(groups) || isEmpty(groups)) {
      return
    }

    // Determine if all groups should be expanded by default based on the total
    // number of layers.
    const numLayersAllGroupsCollapse = Model.get('config.layout.drawer.tabs.layers.numLayersAllGroupsCollapse', 40)
    const numLayers = Model.get('config.layers', []).length

    const expandedByDefault = numLayers < numLayersAllGroupsCollapse

    // console.debug('Groups - expandedByDefault', expandedByDefault, numLayers, numLayersAllGroupsCollapse)

    // Get expanded groups from cookie
    const expandedGroupsCookie = Cookies.get('pozi-state-groups-expanded')
    let expandedGroupsCookieJson = []
    try {
      expandedGroupsCookieJson = [].concat(JSON.parse(expandedGroupsCookie))
    } catch (error) {
      console.log('setGroupsExpandedState - cannot retrieve expandedGroups from cookies: invalid JSON', expandedGroupsCookie)
    }

    // console.debug('setGroupsExpandedState - expandedGroupsCookieJson', expandedGroupsCookieJson, expandedGroupsCookie)

    // Batch set expanded/collapsed state in Redux
    batch(() => {
      // console.debug('setGroupsExpandedState - dispatch batch', groups, Object.values(groups))

      for (const group of Object.values(groups)) {
        const { id } = group

        // Set expanded/collapsed state based on cookie or default
        const groupExpandedFromCookie = expandedGroupsCookieJson.includes(id)
        const expanded = groupExpandedFromCookie || expandedByDefault

        // console.debug('setGroupsExpandedState - group', id, expanded, groupExpandedFromCookie, expandedByDefault)

        if (expanded && !group.expanded) {
          dispatchSetGroupExpanded(group)
        }

        if (!expanded && group.expanded) {
          dispatchSetGroupCollapsed(group)
        }
      }
    })
  }

  render () {
    const { classes, groups } = this.props

    if (isNil(groups) || isEmpty(groups)) {
      return null
    }

    // console.debug('Render layer groups', groups)

    const groupsMarkup = []
    for (const groupid in groups) {
      const group = groups[groupid]
      const key = `${groupid}_${group.expanded}`

      groupsMarkup.push(<Group key={key} groupid={groupid} />)
    }

    return <div className={classes.root}>{groupsMarkup}</div>
  }
}

Groups.propTypes = {
  classes: PropTypes.object.isRequired,
  groups: PropTypes.object.isRequired,
  dispatchSetGroupCollapsed: PropTypes.func.isRequired,
  dispatchSetGroupExpanded: PropTypes.func.isRequired
}

function mapStateToProps (state, ownProps) {
  const { config, groups } = state

  return {
    groups
  }
}

const mapDispatchToProps = dispatch => {
  return {
    dispatchSetGroupExpanded: group => {
      dispatch(setGroupExpanded(group))
    },
    dispatchSetGroupCollapsed: group => {
      dispatch(setGroupCollapsed(group))
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Groups)
  )
)
